import { Component, EventEmitter, Input, Output, ViewEncapsulation } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import {
  BusinessCardElementId,
  BusinessCardLogoSection,
  BusinessCardTextSection,
  BusinessCardTextSize,
  QrCodeRecord
} from './app.types';

export interface BusinessCardDragEvent {
  event: PointerEvent;
  elementId: string;
}

@Component({
  selector: 'app-business-card-renderer',
  standalone: false,
  templateUrl: './business-card-renderer.component.html',
  styleUrl: './business-card-renderer.component.scss',
  encapsulation: ViewEncapsulation.None
})
export class BusinessCardRendererComponent {
  @Input() textSections: BusinessCardTextSection[] = [];
  @Input() logoSections: BusinessCardLogoSection[] = [];
  @Input() qrCodes: QrCodeRecord[] = [];
  @Input() qrImages: Record<number, string> = {};
  @Input() backgroundStyle: Record<string, string> = {};
  @Input() showGrid = false;
  @Input() gridStyle: Record<string, string> = {};
  @Input() showLogoRemove = false;
  @Input({ required: true }) elementStyle!: (elementId: BusinessCardElementId) => Record<string, string>;
  @Input({ required: true }) qrElementId!: (qrCode: QrCodeRecord) => string;

  @Output() editText = new EventEmitter<string>();
  @Output() dragStart = new EventEmitter<BusinessCardDragEvent>();
  @Output() dragMove = new EventEmitter<PointerEvent>();
  @Output() dragStop = new EventEmitter<PointerEvent>();
  @Output() removeLogo = new EventEmitter<string>();

  constructor(private readonly sanitizer: DomSanitizer) {}

  trackTextSection(_index: number, section: BusinessCardTextSection): string {
    return section.id;
  }

  trackLogoSection(_index: number, section: BusinessCardLogoSection): string {
    return section.id;
  }

  textSectionHtml(section: BusinessCardTextSection): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(
      this.sanitizeRichText(section.html || this.textToHtml(section.text))
    );
  }

  textSectionStyle(section: BusinessCardTextSection): Record<string, string> {
    return {
      ...this.elementStyle(section.id),
      fontSize: this.textSizeCssVariable(section.size),
      color: this.textColor(section.color)
    };
  }

  startDrag(event: PointerEvent, elementId: BusinessCardElementId): void {
    this.dragStart.emit({ event, elementId });
  }

  private textColor(value: string | undefined): string {
    return typeof value === 'string' && /^#[0-9a-fA-F]{6}$/.test(value) ? value : 'var(--card-foreground)';
  }

  private textSizeCssVariable(value: BusinessCardTextSize | undefined): string {
    switch (value) {
      case 'small':
        return 'var(--business-card-text-small)';
      case 'large':
        return 'var(--business-card-text-large)';
      case 'xlarge':
        return 'var(--business-card-text-xlarge)';
      default:
        return 'var(--business-card-text-normal)';
    }
  }

  private textToHtml(text: string): string {
    return this.escapeHtml(text).split(/\r?\n/).map((line) => `<div>${line || '<br>'}</div>`).join('');
  }

  private sanitizeRichText(html: string): string {
    const allowedTags = new Set(['B', 'BLOCKQUOTE', 'BR', 'CODE', 'DIV', 'EM', 'H1', 'I', 'LI', 'OL', 'P', 'PRE', 'S', 'SPAN', 'STRONG', 'UL']);
    const allowedFontSizes = new Set(['0.85em', '1.2em', '1.45em']);
    const doc = document.implementation.createHTMLDocument('');
    doc.body.innerHTML = html;

    Array.from(doc.body.querySelectorAll('*')).forEach((element) => {
      if (!allowedTags.has(element.tagName)) {
        element.replaceWith(...Array.from(element.childNodes));
        return;
      }

      Array.from(element.attributes).forEach((attribute) => {
        const name = attribute.name.toLowerCase();
        if (name !== 'style' || element.tagName !== 'SPAN') {
          element.removeAttribute(attribute.name);
          return;
        }

        const fontSize = (element as HTMLElement).style.fontSize;
        if (allowedFontSizes.has(fontSize)) {
          element.setAttribute('style', `font-size: ${fontSize};`);
        } else {
          element.removeAttribute(attribute.name);
        }
      });
    });

    return doc.body.innerHTML.slice(0, 2_000);
  }

  private escapeHtml(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
}
